import React, { createContext, useContext, useReducer } from 'react';
import { supabase } from '../config/supabase';
import { generateGameplan } from '../utils/gameplanGenerator';

const QuestionnaireContext = createContext();

const initialState = {
  responses: {},
  isComplete: false,
  startTime: null,
  completionTime: null,
  clientInfo: {
    name: '',
    email: ''
  }
};

function questionnaireReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_RESPONSE':
      return {
        ...state,
        responses: {
          ...state.responses,
          [action.questionId]: action.value
        }
      };
    case 'UPDATE_CLIENT_INFO':
      return {
        ...state,
        clientInfo: {
          ...state.clientInfo,
          ...action.info
        }
      };
    case 'START_QUESTIONNAIRE':
      return {
        ...state,
        startTime: new Date().toISOString()
      };
    case 'COMPLETE_QUESTIONNAIRE':
      return {
        ...state,
        isComplete: true,
        completionTime: new Date().toISOString()
      };
    case 'RESET_QUESTIONNAIRE':
      return initialState;
    default:
      return state;
  }
}

export function QuestionnaireProvider({ children }) {
  const [state, dispatch] = useReducer(questionnaireReducer, initialState);

  const updateResponse = (questionId, value) => {
    dispatch({ type: 'UPDATE_RESPONSE', questionId, value });
  };

  const updateClientInfo = (info) => {
    dispatch({ type: 'UPDATE_CLIENT_INFO', info });
  };

  const startQuestionnaire = () => {
    dispatch({ type: 'START_QUESTIONNAIRE' });
  };

  const completeQuestionnaire = async () => {
    dispatch({ type: 'COMPLETE_QUESTIONNAIRE' });

    // Generate gameplan
    const gameplanData = generateGameplan(state.responses);

    // Save to database
    try {
      const { data, error } = await supabase
        .from('gameplans')
        .insert([
          {
            client_name: state.clientInfo.name || 'Anonymous',
            client_email: state.clientInfo.email || 'no-email@example.com',
            responses: state.responses,
            gameplan_data: gameplanData,
            status: 'completed',
            completed_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) {
        console.error('Error saving gameplan:', error);
      } else {
        console.log('Gameplan saved successfully:', data);
      }
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  const resetQuestionnaire = () => {
    dispatch({ type: 'RESET_QUESTIONNAIRE' });
  };

  return (
    <QuestionnaireContext.Provider 
      value={{
        ...state,
        updateResponse,
        updateClientInfo,
        startQuestionnaire,
        completeQuestionnaire,
        resetQuestionnaire
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  );
}

export function useQuestionnaire() {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
}